import { lazy, type ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import type { FeatureKey } from '@/types';
import type { Permission } from '@/lib/permissions';
import { AccessDenied, AdminRoute } from '@/features/admin/guards';

/*
 * Every admin screen is its own chunk. Guards mirror features/admin/nav.ts: a route needs BOTH a permission
 * and (where relevant) an enabled tenant module. This is UX only — the API enforces the same rules.
 */
const page = (loader: () => Promise<{ default: ComponentType }>) => lazy(loader);

const Dashboard = page(() => import('@/pages/admin/Dashboard'));
const WorldView = page(() => import('@/pages/admin/WorldView'));
const Products = page(() => import('@/pages/admin/Products'));
const ProductEditor = page(() => import('@/pages/admin/ProductEditor'));
const Categories = page(() => import('@/pages/admin/Categories'));
const Inventory = page(() => import('@/pages/admin/Inventory'));
const Orders = page(() => import('@/pages/admin/Orders'));
const B2BQuotes = page(() => import('@/pages/admin/B2BQuotes'));
const OrderDetail = page(() => import('@/pages/admin/OrderDetail'));
const Customers = page(() => import('@/pages/admin/Customers'));
const Sellers = page(() => import('@/pages/admin/Sellers'));
const CustomerDetail = page(() => import('@/pages/admin/CustomerDetail'));
const Coupons = page(() => import('@/pages/admin/Coupons'));
const Banners = page(() => import('@/pages/admin/Banners'));
const Offers = page(() => import('@/pages/admin/Offers'));
const Campaigns = page(() => import('@/pages/admin/Campaigns'));
const Wallet = page(() => import('@/pages/admin/Wallet'));
const Loyalty = page(() => import('@/pages/admin/Loyalty'));
const Referrals = page(() => import('@/pages/admin/Referrals'));
const StoreSettings = page(() => import('@/pages/admin/StoreSettings'));
const Branding = page(() => import('@/pages/admin/Branding'));
const Navigation = page(() => import('@/pages/admin/Navigation'));
const Theme = page(() => import('@/pages/admin/Theme'));
const Analytics = page(() => import('@/pages/admin/Analytics'));
const Settings = page(() => import('@/pages/admin/Settings'));

const g = (permission: Permission, feature: FeatureKey | undefined, Comp: ComponentType) => (
  <AdminRoute permission={permission} feature={feature}><Comp /></AdminRoute>
);

export const adminChildren: RouteObject[] = [
  { index: true, element: g('dashboard.view', undefined, Dashboard) },
  { path: 'world', element: g('analytics.view', undefined, WorldView) },
  { path: 'products', element: g('catalog.read', 'products', Products) },
  { path: 'products/new', element: g('catalog.write', 'products', ProductEditor) },
  { path: 'products/:id', element: g('catalog.read', 'products', ProductEditor) },
  { path: 'categories', element: g('catalog.read', 'categories', Categories) },
  { path: 'inventory', element: g('catalog.read', 'products', Inventory) },
  { path: 'orders', element: g('orders.read', 'orders', Orders) },
  { path: 'b2b-quotes', element: g('orders.read', 'orders', B2BQuotes) },
  { path: 'orders/:id', element: g('orders.read', 'orders', OrderDetail) },
  { path: 'customers', element: g('customers.read', undefined, Customers) },
  { path: 'sellers', element: g('settings.manage', undefined, Sellers) },
  { path: 'customers/:id', element: g('customers.read', undefined, CustomerDetail) },
  { path: 'coupons', element: g('marketing.manage', 'coupons', Coupons) },
  { path: 'banners', element: g('marketing.manage', 'banners', Banners) },
  { path: 'offers', element: g('marketing.manage', 'coupons', Offers) },
  { path: 'campaigns', element: g('marketing.manage', 'campaigns', Campaigns) },
  { path: 'wallet', element: g('wallet.read', 'wallet', Wallet) },
  { path: 'loyalty', element: g('loyalty.manage', 'loyalty', Loyalty) },
  { path: 'referrals', element: g('referrals.manage', 'affiliate', Referrals) },
  { path: 'store', element: g('store.manage', undefined, StoreSettings) },
  { path: 'branding', element: g('store.manage', undefined, Branding) },
  { path: 'navigation', element: g('store.manage', undefined, Navigation) },
  { path: 'theme', element: g('store.manage', undefined, Theme) },
  { path: 'analytics', element: g('analytics.view', 'analytics', Analytics) },
  { path: 'settings', element: g('settings.manage', undefined, Settings) },
  { path: '*', element: <AccessDenied title="Page not found" description="This admin page doesn't exist." /> },
];

